import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { deferredUpdater, getBoardId, getBoardTitle, getId } from "../utils";
import * as jsonpatch from 'fast-json-patch';
import { Link } from 'react-router-dom';
import { OnlineRowDto, PageDto, PowerBiReportDto, WidgetDto } from './PageModels';
import { OnlineWidget } from './OnlineWidget';
import { Loading } from './Loading';
import { fetchMeasurements, MeasurementsResponse } from '../api/plantyst';
import { apiFetch } from '../api/api';

const widgetTypes = ["online", "powerbi"];

interface State {
    page?: PageDto;
    measurements?: MeasurementsResponse;
    loading: boolean;
}

export class Page extends React.Component<RouteComponentProps, State> {

    page?: PageDto;
    serverPage?: PageDto;
    localWidgetId = -1;
    localOnlineRowId = -1;
    localPowerBiReportId = -1;
    onlineRowKeys: { [id: number]: number; } = {};
    serverWidgetIds: { [id: number]: number; } = {};
    serverOnlineRowIds: { [id: number]: number; } = {};
    serverPowerBiReportIds: { [id: number]: number; } = {};

    constructor(props: RouteComponentProps) {
        super(props);
        this.state = { loading: true };
    }

    componentDidMount() {
        this.init();
    }

    async init() {
        await deferredUpdater.updateNow();
        const boardId = getBoardId(this.props);
        const id = getId(this.props);
        const response = id != null ? await apiFetch(`api/boards/${boardId}/pages/${id}`) :
            await apiFetch(`api/boards/${boardId}/pages`, { method: "POST" });
        const page: PageDto = await response.json();
        const measurements = await fetchMeasurements();
        if (boardId === getBoardId(this.props) && id === getId(this.props)) {
            this.setState({ page, measurements, loading: false });
            this.page = page;
            this.serverPage = page;

            if (id == null) {
                this.props.history.replace(`/boards/${boardId}/pages/${page.pageId}`);
            }
        }
    }

    componentDidUpdate(prevProps: RouteComponentProps, prevState: State) {
        const oldId = getId(prevProps);
        const newId = getId(this.props);
        if (oldId != null && newId != null && oldId !== newId) {
            this.init();
        }
    }

    render() {
        if (this.state.loading) {
            return <Loading />;
        }
        const page = this.state.page;
        if (!page) {
            return "Page failed to load.";
        }
        const widget: WidgetDto | undefined = page.widgets[0];
        const powerBiReport: PowerBiReportDto | undefined = page.powerBiReports?.filter(p => p.powerBiReportId === widget?.powerBiReportId)[0];
        return <div>
            <nav>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to={`/`}>Boards</Link></li>
                    <li className="breadcrumb-item"><Link to={`/boards/${page.boardId}`}>{getBoardTitle({ boardId: page.boardId, title: page.boardTitle })}</Link></li>
                    <li className="breadcrumb-item">Pages</li>
                    <li className="breadcrumb-item active"><Link to={`/boards/${page.boardId}/pages/${page.pageId}`}>{page.order}</Link></li>
                </ol>
            </nav>
            <h3>Parameters</h3>
            <div className="entity-parameters">
                <div className="form-group">
                    <span className="label">Display time [s]</span>
                    <input onBlur={this.sendToServer}
                        onChange={e => this.updateLocal({ displayTime: parseInt(e.target.value) })}
                        value={page.displayTime || ""} className="form-control" />
                </div>
                <div className="form-group">
                    <span className="label">Time zone</span>
                    <select value={page.timeZoneId || ""} className="form-control"
                        onChange={e => { this.updateLocal({ timeZoneId: e.target.value }); this.sendToServer() }}>
                        {page.timeZones.map(zone => <option key={zone.id} value={zone.id}>{zone.name}</option>)}
                    </select>
                </div>
            </div>
            <h3>Content</h3>
            <div className="entity-parameters">
                <div className="form-group">
                    <span className="label">Type</span>
                    <select value={widget?.type || "none"} className="form-control" disabled={widget?.type != null}
                        onChange={e => { this.changeWidgetType(e.target.value as any); this.sendToServer() }}>
                        <option value="none"></option>
                        {widgetTypes.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
                {widget && ["youtube", "stream"].indexOf(widget.type) >= 0 && <div className="form-group">
                        <span className="label">Resource identifier</span>
                        <input onBlur={this.sendToServer}
                            onChange={e => this.updateWidget(widget, { resourceId: e.target.value })}
                            value={widget.resourceId || ""} className="form-control" />
                </div>}
                {widget && widget.type === "online" && <OnlineWidget
                    widget={widget}
                    addOnlineRowLocal={row => this.addOnlineRow(widget, row)}
                    updateOnlineRowLocal={row => this.updateOnlineRow(widget, row)}
                    sendToServer={this.sendToServer}
                    removeOnlineRow={onlineRowId => this.removeOnlineRow(widget, onlineRowId)}
                    onlineRowKeys={this.onlineRowKeys} />}
                {widget && widget.type === "powerbi" && <>
                    <div className="form-group">
                        <span className="label">Report ID</span>
                        <input onBlur={this.sendToServer}
                            onChange={e => this.updatePowerBiReport(powerBiReport, { reportId: e.target.value })}
                            value={powerBiReport.reportId || ""} className="form-control" />
                    </div>
                    <div className="form-group">
                        <span className="label">Refresh period [s]</span>
                        <input onBlur={this.sendToServer}
                            onChange={e => this.updatePowerBiReport(powerBiReport, { refreshPeriodSeconds: parseInt(e.target.value) || 0 })}
                            value={powerBiReport.refreshPeriodSeconds || ""} className="form-control" />
                    </div>
                </>}
            </div>
        </div >;
    }

    addOnlineRow(widget: WidgetDto, row: OnlineRowDto) {
        this.updateWidget(widget, { onlineRows: (widget.onlineRows || []).concat(row) });
    }

    removeOnlineRow(widget: WidgetDto, onlineRowId: number) {
        const onlineRows = widget.onlineRows!.filter(p => p.onlineRowId !== onlineRowId);
        this.updateWidget(widget, { onlineRows });
    }

    updateOnlineRow(widget: WidgetDto, row: OnlineRowDto) {
        const onlineRows = widget.onlineRows!.map(origRow => origRow.onlineRowId === row.onlineRowId ? row : origRow)
        this.updateWidget(widget, { onlineRows });
    }

    updateWidget(widget: WidgetDto, data: Partial<WidgetDto>) {
        const widgets = this.state.page!.widgets
            .map(origWidget => origWidget === widget ? { ...widget, ...data } : origWidget)
        this.updateLocal({ widgets });
    }

    updatePowerBiReport(powerBiReport: PowerBiReportDto, data: Partial<PowerBiReportDto>) {
        const powerBiReports = this.state.page!.powerBiReports
            .map(origPowerBiReport => origPowerBiReport === powerBiReport ? { ...powerBiReport, ...data } : origPowerBiReport)
        this.updateLocal({ powerBiReports });
    }

    async changeWidgetType(type: WidgetDto["type"] | "none") {
        if (type === "none") {
            this.updateLocal({ widgets: [] });
        } else {
            const powerBiReport: PowerBiReportDto | undefined = type === "powerbi" ?
                { powerBiReportId: this.localPowerBiReportId--, refreshPeriodSeconds: 0, reportId: null } :
                undefined;
            this.updateLocal({
                widgets: [{
                    widgetId: this.localWidgetId--,
                    type,
                    powerBiReportId: powerBiReport?.powerBiReportId,
                }],
                powerBiReports: powerBiReport != null ? [powerBiReport] : undefined,
            });
        }
    }

    updateLocal = async (value: Partial<PageDto>) => {
        const page: PageDto = { ...this.page!, ...value };
        this.setState({ page });
        this.page = page;
    }

    sendToServer = async () => {
        deferredUpdater.scheduleUpdate(async () => {
            const page = this.page!;
            const serverPage = this.serverPage!;
            await this.updatePage(page, serverPage);
            await this.updateWidgets(page, serverPage);
            await this.updatePowerBiReports(page, serverPage);
            this.serverPage = page;
        });
    }

    async updatePage(page: PageDto, serverPage: PageDto) {
        const patch = jsonpatch.compare(
            { ...serverPage, widgets: undefined, powerBiReports: undefined },
            { ...page, widgets: undefined, powerBiReports: undefined });
        if (patch.length > 0) {
            await apiFetch(`api/boards/${page.boardId}/pages/${page.pageId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/jsonpatch+json" },
                body: JSON.stringify(patch),
            });
        }
    }

    async updatePowerBiReports(page: PageDto, serverPage: PageDto) {
        const powerBiReportsToUpdate = page.powerBiReports?.filter(p => serverPage.powerBiReports?.some(r => r.powerBiReportId === p.powerBiReportId)) || [];

        for (const powerBiReport of powerBiReportsToUpdate) {
            const serverPowerBiReport = serverPage.powerBiReports.find(p => p.powerBiReportId === powerBiReport.powerBiReportId)!;
            const patch = jsonpatch.compare(
                { ...serverPowerBiReport, powerBiReportId: undefined },
                { ...powerBiReport, powerBiReportId: undefined });
            if (patch.length > 0) {
                const powerBiReportId = this.serverPowerBiReportIds[powerBiReport.powerBiReportId] || powerBiReport.powerBiReportId;
                await apiFetch(`api/powerBiReports/${powerBiReportId}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/jsonpatch+json" },
                    body: JSON.stringify(patch),
                });
            }
        }
    }

    async updateWidgets(page: PageDto, serverPage: PageDto) {
        const widgetsToDelete = serverPage.widgets.filter(p => !page.widgets.some(r => r.widgetId === p.widgetId && r.type === p.type));
        const widgetsToUpdate = page.widgets.filter(p => serverPage.widgets.some(r => r.widgetId === p.widgetId && r.type === p.type));
        const widgetsToCreate = page.widgets.filter(p => !serverPage.widgets.some(r => r.widgetId === p.widgetId && r.type === p.type));

        for (const widget of widgetsToDelete) {
            const widgetId = this.serverWidgetIds[widget.widgetId] || widget.widgetId;
            await apiFetch(`api/boards/${page.boardId}/pages/${page.pageId}/widgets/${widgetId}`, {
                method: "DELETE",
            });
        }

        for (const widget of widgetsToCreate) {
            const response = await apiFetch(`api/boards/${page.boardId}/pages/${page.pageId}/widgets`, {
                method: "POST",
                headers: { "Content-Type": "application/jsonpatch+json" },
                body: JSON.stringify({ ...widget, onlineRows: undefined, widgetId: undefined, powerBiReportId: undefined }),
            });
            const serverWidget: WidgetDto = await response.json();
            this.serverWidgetIds[widget.widgetId] = serverWidget.widgetId;
            if (widget.powerBiReportId != null && serverWidget.powerBiReportId != null) {
                this.serverPowerBiReportIds[widget.powerBiReportId] = serverWidget.powerBiReportId;
            }
            await this.updateOnlineRows(page, widget, serverWidget);
        }

        for (const widget of widgetsToUpdate) {
            const serverWidget = serverPage.widgets.find(p => p.widgetId === widget.widgetId)!;
            const patch = jsonpatch.compare(
                { ...serverWidget, onlineRows: undefined, widgetId: undefined, powerBiReportId: undefined },
                { ...widget, onlineRows: undefined, widgetId: undefined, powerBiReportId: undefined });
            if (patch.length > 0) {
                const widgetId = this.serverWidgetIds[widget.widgetId] || widget.widgetId;
                await apiFetch(`api/boards/${page.boardId}/pages/${page.pageId}/widgets/${widgetId}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/jsonpatch+json" },
                    body: JSON.stringify(patch),
                });
            }
            await this.updateOnlineRows(page, widget, serverWidget);
        }
    }

    async updateOnlineRows(page: PageDto, widget: WidgetDto, serverWidget: WidgetDto) {
        const onlineRows = widget.onlineRows || [];
        const serverOnlineRows = serverWidget.onlineRows || [];
        const onlineRowsToDelete = serverOnlineRows.filter(p => !onlineRows.some(r => r.onlineRowId === p.onlineRowId));
        const onlineRowsToUpdate = onlineRows.filter(p => serverOnlineRows.some(r => r.onlineRowId === p.onlineRowId));
        const onlineRowsToCreate = onlineRows.filter(p => !serverOnlineRows.some(r => r.onlineRowId === p.onlineRowId));

        for (const onlineRow of onlineRowsToDelete) {
            const onlineRowId = this.serverOnlineRowIds[onlineRow.onlineRowId] || onlineRow.onlineRowId;
            await apiFetch(`api/boards/${page.boardId}/pages/${page.pageId}/widgets/${widget.widgetId}/onlineRows/${onlineRowId}`, {
                method: "DELETE",
            });
        }

        for (const onlineRow of onlineRowsToCreate) {
            const widgetId = this.serverWidgetIds[widget.widgetId] || widget.widgetId;
            const response = await apiFetch(`api/boards/${page.boardId}/pages/${page.pageId}/widgets/${widgetId}/onlineRows`, {
                method: "POST",
                headers: { "Content-Type": "application/jsonpatch+json" },
                body: JSON.stringify(onlineRow),
            });
            const serverOnlineRow: OnlineRowDto = await response.json();
            this.onlineRowKeys[serverOnlineRow.onlineRowId] = onlineRow.onlineRowId;
            this.serverOnlineRowIds[onlineRow.onlineRowId] = serverOnlineRow.onlineRowId;
        }

        for (const onlineRow of onlineRowsToUpdate) {
            const serverOnlineRow = serverOnlineRows.find(p => p.onlineRowId === onlineRow.onlineRowId)!;
            const patch = jsonpatch.compare(serverOnlineRow, onlineRow);
            if (patch.length > 0) {
                const widgetId = this.serverWidgetIds[widget.widgetId] || widget.widgetId;
                const onlineRowId = this.serverOnlineRowIds[onlineRow.onlineRowId] || onlineRow.onlineRowId;
                await apiFetch(`api/boards/${page.boardId}/pages/${page.pageId}/widgets/${widgetId}/onlineRows/${onlineRowId}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/jsonpatch+json" },
                    body: JSON.stringify(patch),
                });
            }
        }
    }
}
